import { Component, Inject, Input, OnInit } from '@angular/core';
import { BehaviorSubject, forkJoin, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { SpinnerModal } from '../../../app/shared/modals/status-spinner/status-spinner.service';
import { unsubscribeComponent } from '../../shared/helpers/unsubscribe-decorator/unsubscribe-decorators.helper';
import { IConfirmModalComponentData } from '../../shared/modals/confirm/confirm-modal.component';
import { ModalsService } from '../../shared/modals/modals.service';
import {
  IClusterUserIdentifier,
  IClusterUserModalShellData,
  TClusterUserModalViewMode,
} from '../../shared/modals/user-management/cluster-user/cluster-user-modals.config';
import { IGroupData, IRowData } from '../../shared/models/list-models';
import { ApiService } from '../../shared/services/api-service/api-service';
import { IDropdownOption } from '../../shared/typings/interfaces/design-library.interface';
import { IUser } from '../../shared/typings/interfaces/user.interface';

@Component({
  selector: 'network-settings',
  template: require('./network-settings.component.html'),
  styles: [require('./network-settings.component.scss')],
})
@unsubscribeComponent
export class NetworkSettingsComponent implements OnInit {
  public clusterName: string;
  public clusterId: string;
  public madLibSelection: string;
  public sidebarSelection: string;
  public columns: string[] = ['USER_NAME', 'JOB_TITLE', 'ACCESS_LEVEL', 'SCHOOL_PORTFOLIO', 'MORE'];
  public groupData$: BehaviorSubject<IGroupData> = new BehaviorSubject(null);
  public networkName: string = 'User permissions';
  public option$: BehaviorSubject<IDropdownOption> = new BehaviorSubject(null);
  @Input() currentUser: IUser;

  constructor(
    @Inject('$state') private $state: any,
    @Inject('$stateParams') private $stateParams,
    @Inject('SpinnerModal') private spinnerService: SpinnerModal,
    private apiService: ApiService,
    private modalsService: ModalsService,
  ) {
    // empty block
  }

  ngOnInit(): void {
    this.spinnerService.activateSpinner('Loading...');
    this.madLibSelection = this.$stateParams.grouping ? this.$stateParams.grouping : 'All users';
    forkJoin([this.getMadLib(), this.getUsers()])
      .pipe(
        tap(res => {
          const [madLibs, users] = res;
          this.nextOptions(madLibs);
          this.nextGroupData(users);
        }),
      )
      .subscribe({
        complete: () => this.spinnerService.deactivateSpinner(),
      });
  }

  private getMadLib(): Observable<any> {
    return this.apiService.getClusterUserMadLib({
      projection: this.columns,
    });
  }

  private getUsers(): Observable<any> {
    let grouping;
    if (this.madLibSelection === 'All users') {
      grouping = null;
    } else {
      grouping = this.madLibSelection;
    }
    return this.apiService.getClusterUsers({
      grouping,
      projection: this.columns,
    });
  }

  private nextOptions(madLibs): void {
    const {
      data: {
        UserGrouping: { groupingOptions: options },
      },
    } = madLibs;
    this.option$.next(options);
  }

  private nextGroupData(users): void {
    const {
      data: {
        UserGrouping: { groupData },
      },
    } = users;
    this.groupData$.next(groupData);
  }

  public onBack(route: string): void {
    this.$state.go(route);
  }

  public onMadLibChange(e: string): void {
    this.spinnerService.activateSpinner('Loading...');
    this.madLibSelection = e;
    this.updateUrl();
    forkJoin([this.getUsers()])
      .pipe(
        tap(res => {
          const [users] = res;
          this.nextGroupData(users);
        }),
      )
      .subscribe({
        complete: () => this.spinnerService.deactivateSpinner(),
      });
  }

  private updateUrl(): void {
    let grouping;
    if (this.madLibSelection === 'All users') {
      grouping = null;
    } else {
      grouping = this.madLibSelection;
    }
    this.$state.go('.', { grouping }, { notify: false });
  }

  public onClickedMoreButton({
    action,
    user,
  }: {
    action: 'DETAILS' | 'PORTFOLIO' | 'REMOVE';
    user: IClusterUserIdentifier;
  }): void {
    switch (action) {
      case 'DETAILS':
        this.sendToEditUser({ user });
        break;
      case 'PORTFOLIO':
        this.sendToEditPortfolio({ user });
        break;
      case 'REMOVE':
        this.sendToRemoveUser({ user });
      default:
        break;
    }
  }

  public onClickedAddUser(): void {
    this.sendToCreateUser();
  }

  private sendToEditUser({ user }: { user: IClusterUserIdentifier }): void {
    const mode = 'EDIT';
    const data: IClusterUserModalShellData = {
      projectionColumns: this.columns,
      currentUser: this.currentUser,
      user,
      mode,
    };
    this.modalsService
      .openClusterUserShellModal(data, mode)
      .afterClosed()
      .subscribe(res => {
        if (res) {
          this.onMutateUserReqReturned(res);
        }
      });
  }

  private sendToEditPortfolio({ user }: { user: IClusterUserIdentifier }): void {
    const mode = 'PORTFOLIO';
    const data: IClusterUserModalShellData = {
      projectionColumns: this.columns,
      currentUser: this.currentUser,
      user,
      mode,
    };
    this.modalsService
      .openClusterUserShellModal(data, mode)
      .afterClosed()
      .subscribe(res => {
        if (res) {
          this.onMutateUserReqReturned(res);
        }
      });
  }

  private sendToCreateUser(): void {
    const mode = 'CREATE';
    const data: IClusterUserModalShellData = {
      projectionColumns: this.columns,
      currentUser: this.currentUser,
      mode,
    };
    this.modalsService
      .openClusterUserShellModal(data, mode)
      .afterClosed()
      .subscribe(res => {
        if (res) {
          this.onMutateUserReqReturned(res);
        }
      });
  }

  private sendToRemoveUser({ user }): void {
    const data: IConfirmModalComponentData = {
      title: 'Remove User',
      message: `Are you sure you want to remove ${user.name} from this network?`,
      confirmText: 'Remove',
    };
    this.modalsService
      .openConfirmModal(data)
      .afterClosed()
      .subscribe((confirmed: boolean) => {
        if (confirmed) {
          const userPayload = { userId: user.id };
          const columns = this.columns;
          const mode: TClusterUserModalViewMode = 'REMOVE';
          this.apiService
            .mutateClusterUser({ userPayload, columns }, mode)
            .pipe(
              tap((res: { data: { [mutationName: string]: IRowData[][] }; errors: any[] }) => {
                const { data } = res;
                if (data && Object.keys(data).length) {
                  this.onMutateUserReqReturned(data.deleteClusterUser);
                }
              }),
            )
            .subscribe();
        }
      });
  }

  private onMutateUserReqReturned(res): void {
    const { groupData, groupingOptions: options } = res;
    this.option$.next(options);
    this.groupData$.next(groupData);
    this.madLibSelection = 'All users';
    this.updateUrl();
  }
}
